import React, { useState, useRef, useMemo, useEffect } from "react";
import { Plus, X, BookOpen, Edit2 } from "lucide-react";
import Swal from "sweetalert2";
import JoditEditor from 'jodit-react';
import axios from 'axios';

// Base URL for API
const API_BASE_URL = 'https://backend.dlapakistan.com/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

const MembershipSupportPage = () => {
  const [content, setContent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);

  const [form, setForm] = useState({
    heading: "",  // Changed from title to heading
    content: ""
  });

  const editorRef = useRef(null);
  const emerald600 = "#059669";

  const config = useMemo(() => ({
    readonly: false,
    height: 300,
    theme: "default",
    toolbarButtonSize: "medium",
    removeButtons: ['source', 'fullsize', 'about', 'print', 'preview', 'find'],
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough',
      'ul', 'ol', 'outdent', 'indent',
      'font', 'fontsize', 'brush', 'paragraph',
      'image', 'table', 'link', 'align',
      'undo', 'redo', 'hr', 'eraser', 'copyformat',
      'selectall'
    ],
    style: { background: '#f9fafb' },
    uploader: { insertImageAsBase64URI: true },
    placeholder: 'Content here...',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    disablePlugins: ['paste', 'pasteStorage'],
  }), []);

  // Fetch data from backend
  useEffect(() => {
    fetchMembershipSupportData();
  }, []);

  const fetchMembershipSupportData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/membership-support/');
      console.log("GET Membership Support Response:", response.data);
      
      if (response.data && response.data.heading) {
        setContent(response.data);
        setHasData(true);
        localStorage.setItem("membershipSupport_Storage", JSON.stringify(response.data));
      } else {
        setHasData(false);
        setContent(null);
        const savedData = localStorage.getItem("membershipSupport_Storage");
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          if (parsedData && parsedData.heading) {
            setContent(parsedData);
            setHasData(true);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching membership support data:", error);
      
      if (error.response?.status === 404) {
        setHasData(false);
        setContent(null);
      } else {
        const savedData = localStorage.getItem("membershipSupport_Storage");
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          if (parsedData && parsedData.heading) {
            setContent(parsedData);
            setHasData(true);
          }
        }
        
        if (error.response?.status !== 404) {
          Swal.fire({
            icon: "warning",
            title: "Connection Issue",
            text: "Could not connect to server. Using cached data.",
            confirmButtonColor: emerald600
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setForm({ heading: "", content: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.heading.trim() || !form.content.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please fill in both heading and content.",
        confirmButtonColor: emerald600
      });
      return;
    }

    setSaving(true);

    try {
      const dataToSend = {
        heading: form.heading.trim(),
        content: form.content.trim()
      };

      console.log("Sending membership support data:", dataToSend);

      let response;
      
      if (!hasData) {
        // Create new - POST request
        response = await api.post('/membership-support/', dataToSend);
        setHasData(true);
      } else {
        // Update existing - PUT request
        response = await api.put('/membership-support/', dataToSend);
      }

      // Update state with new data
      setContent(response.data);
      localStorage.setItem("membershipSupport_Storage", JSON.stringify(response.data));

      Swal.fire({
        icon: "success",
        title: !hasData ? "Created Successfully!" : "Updated Successfully!",
        showConfirmButton: false,
        timer: 1500
      });

      closeModal();
    } catch (error) {
      console.error("Error saving membership support data:", error);
      
      let errorMessage = "An error occurred while saving.";
      
      if (error.response) {
        switch (error.response.status) {
          case 400:
            if (!hasData && error.response.data?.detail?.includes("already exists")) {
              errorMessage = "Content already exists. Please refresh the page.";
              setHasData(true);
            } else {
              errorMessage = error.response.data?.detail || "Invalid data provided.";
            }
            break;
          case 404:
            if (!hasData) {
              errorMessage = "Server error: POST request returned 404. Please try again.";
            } else {
              errorMessage = "Content not found for update. Please create new content.";
            }
            break;
          default:
            errorMessage = `Error ${error.response.status}: ${error.response.data?.detail || 'Unknown error'}`;
        }
      } else if (error.request) {
        errorMessage = "No response from server. Please check your connection.";
        
        // Save to localStorage as fallback
        const fallbackData = {
          id: Date.now(),
          ...form,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setContent(fallbackData);
        setHasData(true);
        localStorage.setItem("membershipSupport_Storage", JSON.stringify(fallbackData));
        
        Swal.fire({
          icon: "warning",
          title: "Saved Locally",
          text: "Data saved to local storage (offline mode).",
          confirmButtonColor: emerald600
        });
        closeModal();
        return;
      }
      
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: errorMessage,
        confirmButtonColor: emerald600
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    if (content) {
      setForm({
        heading: content.heading || "",
        content: content.content || ""
      });
      setShowModal(true);
    }
  };

  const handleAddNew = () => {
    setForm({ heading: "", content: "" });
    setShowModal(true);
  };

  const handleEditorChange = (newContent) => {
    setForm(prev => ({ ...prev, content: newContent }));
  };

  if (loading) {
    return (
      <div className="flex flex-col mt-20 min-h-screen w-full items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        <p className="mt-4 text-gray-600">Loading membership support content...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full">
      {/* HEADER */}
      <div className="px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mt-12 md:mt-0">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-900">Membership Support Management</h1>
            <p className="text-gray-600 mt-1">Manage membership support content from database</p>
          </div>
          <button
            onClick={hasData ? handleEdit : handleAddNew}
            style={{ backgroundColor: emerald600 }}
            className="flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all"
          >
            {hasData ? (
              <><Edit2 className="w-4 h-4 mr-2" /> Edit Content</>
            ) : (
              <><Plus className="w-5 h-5 mr-2" /> Add Content</>
            )}
          </button>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="px-8 md:px-12 mt-16 w-full pb-20">
        {!hasData ? (
          <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl">
            <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-400 text-lg">No content added yet.</p>
            <button
              onClick={handleAddNew}
              style={{ backgroundColor: emerald600 }}
              className="mt-4 px-6 py-2 rounded-lg text-white font-medium hover:opacity-90 transition-all"
            >
              Add Content
            </button>
          </div>
        ) : (
          <div className="animate-fadeIn text-left w-full bg-transparent">
            <div className="mb-10 border-l-4 pl-6" style={{ borderColor: emerald600 }}>
              <h2 className="text-4xl font-bold text-gray-900 tracking-tight">{content?.heading || "No Title"}</h2>
            </div>
            <div className="mt-12 border-t border-gray-200 pt-10 w-full bg-transparent">
              <div
                className="text-gray-600 text-lg leading-loose space-y-6 custom-description w-full prose prose-emerald max-w-none bg-transparent"
                dangerouslySetInnerHTML={{ __html: content?.content || "" }}
              />
            </div>
          </div>
        )}
      </div>

      {/* MODAL */}
      {showModal && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl pointer-events-auto overflow-hidden animate-modalIn border border-gray-100">
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-800">{hasData ? "Update Content" : "Create Content"}</h2>
                <button onClick={closeModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto max-h-[80vh]">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Heading</label>
                  <input 
                    value={form.heading} 
                    onChange={(e) => setForm({...form, heading: e.target.value})} 
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 transition-all" 
                    style={{ '--tw-ring-color': emerald600 }} 
                    required 
                    disabled={saving}
                    placeholder="Enter heading/title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Content</label>
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden jodit-editor-container">
                    <JoditEditor
                      ref={editorRef}
                      value={form.content}
                      config={config}
                      onChange={handleEditorChange}
                    />
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button 
                    type="button"
                    onClick={closeModal}
                    className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-2xl font-medium hover:bg-gray-50 transition-all"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    style={{ backgroundColor: emerald600 }} 
                    className={`flex-1 py-3 text-white rounded-2xl font-bold shadow-lg hover:brightness-110 transition-all flex items-center justify-center ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : hasData ? 'Update Content' : 'Create Content'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes modalIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-modalIn { animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .custom-description * { background-color: transparent !important; border: none !important; }
        .custom-description ul { list-style-type: disc; padding-left: 1.5rem; }
        .custom-description ol { list-style-type: decimal; padding-left: 1.5rem; }
        .jodit-editor-container :global(.jodit-container) { border: none !important; background: #f9fafb !important; min-height: 300px; }
        .jodit-editor-container :global(.jodit-workplace) { background: #f9fafb !important; min-height: 250px; }
        .jodit-editor-container :global(.jodit-toolbar__box) { background: #f9fafb !important; border-bottom: 1px solid #eee !important; }
        .jodit-editor-container :global(.jodit-wysiwyg) { background: #f9fafb !important; padding: 16px !important; min-height: 200px; }
      `}</style>
    </div>
  );
};

export default MembershipSupportPage;